import { useMemo, useState } from 'react';
import { useOutletContext } from 'react-router-dom';
import { ETAPES, ETAPE_INFO, nombreHotesNecessaire, type Etape } from '@dinertournant/core';
import { BandeauAlerte, Bascule, Dialogue, FoyerForm, IconeEnveloppe, PilluleEtape } from '../../components/index.js';
import { useChargement } from '../../hooks/useChargement.js';
import { api, apiDel, apiPatch, dateLongue, joursAvant, type FoyerOrganisateur, type ListeFoyers } from '../../lib/api.js';
import type { ContexteEvenement } from './EvenementLayout.js';

type Filtre = 'tous' | 'hotes' | 'regimes' | 'absents';

function presents(f: FoyerOrganisateur) {
  return f.membres.filter((m) => m.participe);
}

/** « Claire : sans gluten · Karim : sans porc » */
function regimeDe(f: FoyerOrganisateur): string {
  const parts = f.membres.filter((m) => m.participe && m.regime).map((m) => (f.membres.length > 1 ? `${m.prenomNom.split(/\s+/)[0]} : ${m.regime}` : m.regime!));
  return [...new Set(parts)].join(' · ');
}

/** « 28 octobre à 14:05 » */
function dateEnvoi(iso: string): string {
  return new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
}

export function texteSources(foyers: FoyerOrganisateur[]): string {
  const n = (s: string) => foyers.filter((f) => f.source === s).length;
  const pl = (k: number) => (k > 1 ? 's' : '');
  const morceaux = [`${n('en_ligne')} voiture${pl(n('en_ligne'))} inscrite${pl(n('en_ligne'))} en ligne`];
  if (n('manuel')) morceaux.push(`${n('manuel')} ajoutée${pl(n('manuel'))} à la main`);
  if (n('csv')) morceaux.push(`${n('csv')} importée${pl(n('csv'))}`);
  return morceaux.join(' · ');
}

interface ResultatImport {
  importes: number;
  erreurs: { ligne: number; message: string }[];
  total: number;
}

/** Écran 3 : participants (organisateur, desktop fluide). */
export function Participants() {
  const { detail, recharger: rechargerEvenement } = useOutletContext<ContexteEvenement>();
  const eventId = detail.event.id;
  const { donnees, erreur, recharger, remplacer } = useChargement(() => api.get<ListeFoyers>(`/api/events/${eventId}/households`), [eventId]);
  const [filtre, setFiltre] = useState<Filtre>('tous');
  const [recherche, setRecherche] = useState('');
  const [dialogue, setDialogue] = useState<'aucun' | 'ajout' | 'import' | 'lien'>('aucun');
  const [edition, setEdition] = useState<FoyerOrganisateur | null>(null);
  const [csv, setCsv] = useState('');
  const [resultatImport, setResultatImport] = useState<ResultatImport | null>(null);
  const [erreurAction, setErreurAction] = useState<string | null>(null);
  const [copie, setCopie] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  const foyers = donnees?.foyers ?? [];
  /** E-mails distincts du foyer : ceux des personnes, puis le contact du foyer. */
  const emailsDe = (f: FoyerOrganisateur) => [...new Set([...f.membres.map((m) => m.email), f.email].filter((e): e is string => !!e))];
  const stats = useMemo(() => {
    const participants = foyers.reduce((s, f) => s + presents(f).length, 0);
    const inscrits = foyers.reduce((s, f) => s + f.membres.length, 0);
    const aDeux = foyers.filter((f) => f.membres.length === 2).length;
    const aTrois = foyers.filter((f) => f.membres.length >= 3).length;
    const volontaires = foyers.filter((f) => f.veutRecevoir && presents(f).length > 0);
    const parEtape = Object.fromEntries(ETAPES.map((e) => [e, volontaires.filter((f) => f.disponibilites.includes(e)).length])) as Record<Etape, number>;
    return { participants, inscrits, absents: inscrits - participants, aDeux, aTrois, seuls: foyers.length - aDeux - aTrois, volontaires: volontaires.length, parEtape, ideal: nombreHotesNecessaire(participants, { min: detail.event.tailleMin, max: detail.event.tailleMax }, volontaires as never) };
  }, [foyers, detail.event.tailleMin, detail.event.tailleMax]);

  const filtres = useMemo(() => {
    const q = recherche.trim().toLowerCase();
    return foyers.filter((f) => {
      if (filtre === 'hotes' && !f.veutRecevoir) return false;
      if (filtre === 'regimes' && !regimeDe(f)) return false;
      if (filtre === 'absents' && f.membres.every((m) => m.participe)) return false;
      if (!q) return true;
      const texte = [f.nom, f.email, f.telephone, f.adresse, ...f.membres.map((m) => m.prenomNom)].filter(Boolean).join(' ').toLowerCase();
      return texte.includes(q);
    });
  }, [foyers, filtre, recherche]);

  const compte = (k: Filtre) =>
    k === 'tous' ? foyers.length : k === 'hotes' ? foyers.filter((f) => f.veutRecevoir).length : k === 'regimes' ? foyers.filter((f) => !!regimeDe(f)).length : foyers.filter((f) => f.membres.some((m) => !m.participe)).length;

  const majFoyer = (f: FoyerOrganisateur) => {
    if (!donnees) return;
    const existe = donnees.foyers.some((x) => x.id === f.id);
    const liste = existe ? donnees.foyers.map((x) => (x.id === f.id ? f : x)) : [...donnees.foyers, f];
    remplacer({ ...donnees, foyers: liste, participants: liste.reduce((s, x) => s + presents(x).length, 0) });
    void rechargerEvenement();
  };

  const basculer = async (f: FoyerOrganisateur, pid: string, participe: boolean) => {
    setErreurAction(null);
    try {
      const r = await apiPatch<{ foyer: FoyerOrganisateur; hotesRetires?: string[] }>(`/api/events/${eventId}/households/${f.id}/participants/${pid}`, { participe });
      majFoyer(r.foyer);
      if (r.hotesRetires?.length) setMessage(`${f.nom} n'est plus hôte : plus personne ne vient de cette voiture. Vérifiez l'étape « Hôtes par étape ».`);
    } catch (e) {
      setErreurAction((e as Error).message);
    }
  };

  const repartitionFaite = !!detail.event.repartitionGenereeAt;

  const renvoyerConfirmation = async (f: FoyerOrganisateur) => {
    const adresses = emailsDe(f);
    if (!window.confirm(`Renvoyer la confirmation d'inscription à ${f.nom} (${adresses.join(', ')}) ? Un nouveau lien de modification est créé : l'ancien ne fonctionnera plus.`)) return;
    setErreurAction(null);
    setMessage(null);
    try {
      const r = await api.post<{ confirmations: number; foyer: FoyerOrganisateur }>(`/api/events/${eventId}/households/${f.id}/confirmation`, {});
      majFoyer(r.foyer);
      setMessage(`Confirmation renvoyée à ${f.nom} : ${r.confirmations} adresse${r.confirmations > 1 ? 's' : ''}.`);
    } catch (e) {
      setErreurAction((e as Error).message);
    }
  };

  const supprimer = async (f: FoyerOrganisateur) => {
    if (!window.confirm(`Supprimer ${f.nom} et ${f.membres.length > 1 ? 'ses 2 personnes' : 'sa personne'} ? Cette action est définitive.`)) return;
    try {
      await apiDel(`/api/events/${eventId}/households/${f.id}`);
      await recharger();
      void rechargerEvenement();
    } catch (e) {
      setErreurAction((e as Error).message);
    }
  };

  const importer = async () => {
    setErreurAction(null);
    try {
      const r = await api.post<ResultatImport>(`/api/events/${eventId}/households/import`, { csv });
      setResultatImport(r);
      await recharger();
      void rechargerEvenement();
    } catch (e) {
      setErreurAction((e as Error).message);
    }
  };

  const chargerDemo = async () => {
    setErreurAction(null);
    try {
      const r = await api.post<{ ajoutes: number; foyers: number; hotes: number }>(`/api/events/${eventId}/households/demo`, {});
      await recharger();
      void rechargerEvenement();
      setMessage(r.ajoutes ? `${r.ajoutes} voitures de test ajoutées, ${r.hotes} hôtes proposés. Passez à l'étape « Hôtes par étape ».` : 'Le jeu de test est déjà chargé.');
    } catch (e) {
      setErreurAction((e as Error).message);
    }
  };

  const reinitialiser = async () => {
    if (!window.confirm(`Supprimer les ${foyers.length} voitures inscrites, les hôtes et la répartition de « ${detail.event.nom} » ? Cette action est définitive.`)) return;
    setErreurAction(null);
    try {
      const r = await apiDel<{ supprimes: number }>(`/api/events/${eventId}/households`);
      await recharger();
      void rechargerEvenement();
      setMessage(`${r.supprimes} voiture${r.supprimes > 1 ? 's' : ''} supprimée${r.supprimes > 1 ? 's' : ''}. Les inscriptions repartent de zéro.`);
    } catch (e) {
      setErreurAction((e as Error).message);
    }
  };

  const copierLien = async () => {
    try {
      await navigator.clipboard.writeText(detail.liens.inscription);
      setCopie(true);
      setTimeout(() => setCopie(false), 2000);
    } catch {
      setDialogue('lien');
    }
  };

  const jours = joursAvant(detail.event.dateLimiteInscription);
  const complet = !!detail.event.capaciteMax && stats.participants >= detail.event.capaciteMax;
  const ouvertes = jours >= 0 && !complet;
  const placesIdeales = stats.ideal * 3;

  return (
    <div className="page-app">
      <div className="entete-page">
        <div>
          <h1 className="display">Participants</h1>
          <p className="texte-doux ligne" style={{ gap: 8, flexWrap: 'wrap' }}>
            <span className={`point-etat${ouvertes ? ' ok' : ''}`} aria-hidden="true" />
            <strong style={{ color: ouvertes ? 'var(--ok-fg)' : 'var(--muted)' }}>{ouvertes ? 'Inscriptions ouvertes' : jours < 0 ? 'Inscriptions closes' : 'Événement complet'}</strong>
            <span>
              · clôture le {dateLongue(detail.event.dateLimiteInscription)}
              {jours > 0 ? `, dans ${jours} jour${jours > 1 ? 's' : ''}` : jours === 0 ? ', ce soir' : ''}
            </span>
          </p>
        </div>
        <div className="ligne" style={{ flexWrap: 'wrap' }}>
          <button type="button" className="btn" onClick={copierLien} aria-live="polite">
            {copie ? 'Lien copié !' : "Lien d'inscription"}
          </button>
          <button
            type="button"
            className="btn"
            onClick={() => {
              setResultatImport(null);
              setCsv('');
              setDialogue('import');
            }}
          >
            Importer un CSV
          </button>
          <button
            type="button"
            className="btn principal"
            onClick={() => {
              setEdition(null);
              setDialogue('ajout');
            }}
          >
            
            + Ajouter une voiture
          </button>
        </div>
      </div>

      {erreur ? <BandeauAlerte niveau="attention" role="alert" titre={erreur} /> : null}
      {erreurAction ? <BandeauAlerte niveau="attention" role="alert" titre={erreurAction} /> : null}
      {message ? <BandeauAlerte niveau="ok" role="status" titre={message} /> : null}

      <div className="cartes-stats stats-participants">
        <div className="carte stat">
          <span className="valeur">{stats.participants}</span>
          <span className="libelle">participants présents</span>
          <span className="detail">
            {stats.inscrits} inscrit{stats.inscrits > 1 ? 's' : ''}
            {stats.absents ? ` · ${stats.absents} ne participe${stats.absents > 1 ? 'nt' : ''} pas` : ''}
          </span>
          {detail.event.capaciteMax ? (
            <>
              <div className="jauge" role="progressbar" aria-valuenow={stats.participants} aria-valuemin={0} aria-valuemax={detail.event.capaciteMax} aria-label="Places occupées">
                <span style={{ width: `${Math.min(100, (stats.participants / detail.event.capaciteMax) * 100)}%` }} />
              </div>
              <span className="detail">
                {stats.participants} / {detail.event.capaciteMax} places
              </span>
            </>
          ) : null}
        </div>
        <div className="carte stat">
          <span className="valeur">{foyers.length}</span>
          <span className="libelle">voitures</span>
          <span className="detail">
            {stats.aTrois ? `${stats.aTrois} à trois · ` : ''}
            {stats.aDeux} à deux · {stats.seuls} seul{stats.seuls > 1 ? 's' : ''}
          </span>
        </div>
        <div className="carte stat">
          <span className="valeur">{stats.volontaires}</span>
          <span className="libelle">hôtes volontaires</span>
          <span className="detail">sur {foyers.length}  voitures</span>
        </div>
        <div className="carte stat couverture">
          <span className="libelle" style={{ fontWeight: 700, color: 'var(--ink)' }}>
            Couverture des étapes
          </span>
          <span className="detail">
            Hôtes disponibles par étape · il en faudrait environ {stats.ideal} (tables de {detail.event.tailleMin} à {detail.event.tailleMax})
          </span>
          <div className="barres-couverture">
            {ETAPES.map((e) => (
              <div key={e} className={`etape-${e}`}>
                <div className="ligne" style={{ justifyContent: 'space-between', fontSize: 14 }}>
                  <strong style={{ color: 'var(--stage-dark)' }}>{ETAPE_INFO[e].label}</strong>
                  <strong>{stats.parEtape[e]}</strong>
                </div>
                <div className="segments-couverture" aria-hidden="true">
                  {Array.from({ length: Math.max(stats.ideal, stats.parEtape[e], 1) }).map((_, i) => (
                    <span key={i} className={i < stats.parEtape[e] ? 'plein' : ''} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {foyers.length > 0 ? (
        <BandeauAlerte niveau="info">
          Les disponibilités sont des <strong>possibilités</strong>  : chaque voiture hôte ne recevra qu'à une seule étape.{' '}
          {stats.volontaires < placesIdeales
            ? `${stats.volontaires} volontaire${stats.volontaires > 1 ? 's' : ''} pour environ ${placesIdeales} places d'hôte idéales : les groupes seront plutôt vers ${detail.event.tailleMax} personnes.`
            : `${stats.volontaires} volontaires pour environ ${placesIdeales} places d'hôte idéales : de quoi faire des groupes de ${detail.event.tailleMin} à ${detail.event.tailleMax}.`}
        </BandeauAlerte>
      ) : null}

      <section className="carte" style={{ padding: 0 }} aria-label="Liste des voitures">
        <div className="barre-filtres">
          <div className="ligne" style={{ gap: 8, flexWrap: 'wrap' }} role="group" aria-label="Filtrer">
            {(
              [
                ['tous', 'Tous'],
                ['hotes', 'Hôtes'],
                ['regimes', 'Régimes'],
                ['absents', 'Absents'],
              ] as [Filtre, string][]
            ).map(([k, l]) => (
              <button key={k} type="button" className="chip" aria-pressed={filtre === k} onClick={() => setFiltre(k)}>
                {l} · {compte(k)}
              </button>
            ))}
          </div>
          <label className="recherche">
            <span className="sr-only">Rechercher</span>
            <input className="f-input" type="search" placeholder="Rechercher un nom, une rue…" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
          </label>
        </div>

        <div className="tableau-foyers" role="table" aria-label="Voitures">
          <div className="trow th" role="row">
            <span role="columnheader">Voiture</span>
            <span role="columnheader">Personnes · participe</span>
            <span role="columnheader">Contact</span>
            <span role="columnheader">Adresse</span>
            <span role="columnheader">Régime</span>
            <span role="columnheader">Hôte</span>
            <span role="columnheader">Peut recevoir</span>
            <span role="columnheader">
              <span className="sr-only">Actions</span>
            </span>
          </div>
          {filtres.length === 0 ? (
            <p className="texte-muet" style={{ padding: 20 }}>
              {foyers.length === 0 ? "Aucune voiture pour l'instant : partagez le lien d'inscription, importez un CSV ou ajoutez une voiture à la main." : 'Aucune voiture ne correspond.'}
            </p>
          ) : null}
          {filtres.map((f) => {
            const nbPresents = presents(f).length;
            const regime = regimeDe(f);
            return (
              <div key={f.id} className={`trow${nbPresents < f.membres.length ? ' partiel' : ''}`} role="row">
                <div role="cell">
                  <strong>{f.nom.replace(/^les\s+/i, '')}</strong>
                  <div className="texte-muet" style={{ fontSize: 13 }}>
                    {f.membres.length === 1 ? '1 personne' : nbPresents === f.membres.length ? `${f.membres.length} personnes` : `${nbPresents} présente${nbPresents > 1 ? 's' : ''} sur ${f.membres.length}`}
                  </div>
                </div>
                <div role="cell" className={`personnes${f.membres.length >= 2 ? ' couple' : ''}`}>
                  {f.membres.map((m) => (
                    <div key={m.id} className="ligne" style={{ gap: 10 }}>
                      <Bascule actif={m.participe} label={`${m.prenomNom}${m.participe ? ' participe' : ' ne participe pas'}`} onChange={(v) => basculer(f, m.id, v)} />
                      <span className={m.participe ? '' : 'absent'}>{m.prenomNom}</span>
                      {!m.participe ? (
                        <em className="texte-muet" style={{ fontSize: 13 }}>
                          ne participe pas
                        </em>
                      ) : null}
                    </div>
                  ))}
                </div>
                <div role="cell" className="texte-doux">
                  {emailsDe(f).map((e) => (
                    <div key={e}>{e}</div>
                  ))}
                  {f.telephone ? <div>{f.telephone}</div> : null}
                  {emailsDe(f).length === 0 && !f.telephone ? <span className="texte-muet">—</span> : null}
                  {emailsDe(f).length ? (
                    <div className="texte-muet" style={{ fontSize: 12 }}>
                      {f.derniereConfirmation ? `Confirmation envoyée le ${dateEnvoi(f.derniereConfirmation)}` : 'Aucune confirmation envoyée'}
                    </div>
                  ) : null}
                </div>
                <div role="cell" className="texte-doux">
                  {f.adresse ?? <span className="texte-muet">—</span>}
                  {f.complementAdresse ? (
                    <div className="texte-muet" style={{ fontSize: 13 }}>
                      {f.complementAdresse}
                    </div>
                  ) : null}
                </div>
                <div role="cell">{regime || <span className="texte-muet">—</span>}</div>
                <div role="cell">
                  {f.veutRecevoir ? 'Oui' : 'Non'}
                  {f.veutRecevoir && f.capaciteMax ? (
                    <div className="texte-muet" style={{ fontSize: 12 }}>
                      max {f.capaciteMax}
                    </div>
                  ) : null}
                </div>
                <div role="cell" className="ligne" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {ETAPES.map((e) => (
                    <PilluleEtape key={e} etape={e} etat={f.disponibilites.includes(e) ? 'pleine' : 'vide'} />
                  ))}
                </div>
                <div role="cell" className="ligne" style={{ gap: 4, flexWrap: 'nowrap' }}>
                  <button
                    type="button"
                    className="btn petit"
                    onClick={() => {
                      setEdition(f);
                      setDialogue('ajout');
                    }}
                    aria-label={`Modifier la voiture ${f.nom}`}
                  >
                    Modifier
                  </button>
                  <button
                    type="button"
                    className="btn petit"
                    onClick={() => renvoyerConfirmation(f)}
                    disabled={repartitionFaite || emailsDe(f).length === 0}
                    aria-label={`Renvoyer la confirmation à la voiture ${f.nom}`}
                    title={repartitionFaite ? 'Répartition faite : renvoyez le parcours depuis l’écran Diffusion' : emailsDe(f).length === 0 ? 'Aucune adresse e-mail pour cette voiture' : 'Renvoyer la confirmation d’inscription'}
                  >
                    <IconeEnveloppe taille={16} />
                  </button>
                  <button type="button" className="btn petit" onClick={() => supprimer(f)} aria-label={`Supprimer la voiture ${f.nom}`}>
                    ✕
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        <div className="pied-tableau texte-muet">
          <span>{texteSources(foyers)}</span>
          <span>Les effectifs ne comptent que les personnes qui participent.</span>
        </div>
        <div className="pied-tableau outils-test">
          <span className="texte-muet">Outils de test</span>
          <div className="ligne" style={{ gap: 8, flexWrap: 'wrap' }}>
            <button type="button" className="btn petit" onClick={chargerDemo}>
              
              Charger un jeu de test (15 voitures + hôtes)
            </button>
            <button type="button" className="btn petit" onClick={reinitialiser} disabled={foyers.length === 0}>
              Réinitialiser les inscriptions
            </button>
          </div>
        </div>
      </section>

      <Dialogue ouvert={dialogue === 'ajout'} titre={edition ? `Modifier ${edition.nom}` : 'Ajouter une voiture'} onFermer={() => setDialogue('aucun')}>
        <FoyerForm
          eventId={eventId}
          foyer={edition ?? undefined}
          onAnnuler={() => setDialogue('aucun')}
          onEnregistre={(f, confirmations) => {
            majFoyer(f);
            setDialogue('aucun');
            if (confirmations) setMessage(`Voiture « ${f.nom} » ajoutée : confirmation envoyée à ${confirmations} adresse${confirmations > 1 ? 's' : ''}.`);
          }}
        />
      </Dialogue>

      <Dialogue ouvert={dialogue === 'import'} titre="Importer un CSV" onFermer={() => setDialogue('aucun')} large>
        <div className="pile">
          <p className="texte-doux">
            
            Une ligne d'en-tête, puis une voiture par ligne. Colonnes reconnues : <code>voiture, personne1, personne2, email, telephone, adresse, complement, regime, veut_recevoir, disponibilites</code> (séparateur <code>;</code> ou <code>,</code>). Les disponibilités s'écrivent <code>entrée|plat|dessert</code>.
          </p>
          <label className="pile-6">
            <span className="f-label">Fichier</span>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(e) => {
                const fichier = e.target.files?.[0];
                if (fichier) void fichier.text().then(setCsv);
              }}
            />
          </label>
          <label className="pile-6">
            <span className="f-label">Ou collez le contenu</span>
            <textarea
              className="f-input"
              rows={8}
              value={csv}
              onChange={(e) => setCsv(e.target.value)}
              placeholder={'voiture;personne1;personne2;email;telephone;adresse;complement;regime;veut_recevoir;disponibilites\nMartin;Claire Martin;Julien Martin;claire@exemple.fr;06 00;14 rue Notre-Dame;2e étage;Claire : sans gluten;oui;entrée|plat'}
            />
          </label>
          {resultatImport ? (
            <BandeauAlerte niveau={resultatImport.erreurs.length ? 'attention' : 'ok'} titre={`${resultatImport.importes} voiture${resultatImport.importes > 1 ? 's' : ''} importé${resultatImport.importes > 1 ? 's' : ''} sur ${resultatImport.total}.`}>
              {resultatImport.erreurs.length ? (
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {resultatImport.erreurs.map((e) => (
                    <li key={e.ligne}>
                      Ligne {e.ligne} : {e.message}
                    </li>
                  ))}
                </ul>
              ) : null}
            </BandeauAlerte>
          ) : null}
          <div className="ligne" style={{ justifyContent: 'space-between', flexWrap: 'wrap' }}>
            <a className="btn" href={`/api/events/${eventId}/households/export.csv`} download>
              Exporter les participants (CSV)
            </a>
            <div className="ligne">
              <button type="button" className="btn" onClick={() => setDialogue('aucun')}>
                Fermer
              </button>
              <button type="button" className="btn principal" disabled={!csv.trim()} onClick={importer}>
                Importer
              </button>
            </div>
          </div>
        </div>
      </Dialogue>

      <Dialogue ouvert={dialogue === 'lien'} titre="Lien d'inscription" onFermer={() => setDialogue('aucun')}>
        <code className="f-input" style={{ display: 'block' }}>
          {detail.liens.inscription}
        </code>
      </Dialogue>
    </div>
  );
}
